export const ALERT_LIST_PATH = "/alerts";

export const ALERT_CREATE_STEP = {
    INFORMATION: 0,
    METRIC: 1,
    SCOPE: 2,
    DATA: 3,
    SETTINGS: 4,
    RECIPIENTS: 5,
    SUMMARY: 6,
} as const;

export const ALERT_CREATE_STEP_LABEL_KEYS = [
    "alert.create.step.information",
    "alert.create.step.metric",
    "alert.create.step.scope",
    "alert.create.step.data",
    "alert.create.step.settings",
    "alert.create.step.recipients",
    "alert.create.step.summary",
] as const;

export const ALERT_CREATE_STEP_COUNT = ALERT_CREATE_STEP_LABEL_KEYS.length;

export const alertDetailPath = (uuid: string): string => `${ALERT_LIST_PATH}/${uuid}`;

export const ALERT_NAV_STATE_OPEN_CREATE = "openCreateDialog";
export const ALERT_NAV_STATE_OPEN_EDIT = "openEditDialogUuid";

export const ALERT_THRESHOLD_TYPE_CODE = "threshold";
export const ALERT_SEVERITY_OPTION = "ALERT_SEVERITY_OPTION";
export const ALERT_STATUS_OPTION = "ALERT_STATUS_OPTION";
export const ALERT_RUN_FREQUENCY_OPTION = "ALERT_RUN_FREQUENCY_OPTION";
export const ALERT_EVALUATION_PERIOD_OPTION = "ALERT_EVALUATION_PERIOD_OPTION";
export const ALERT_THRESHOLD_CONDITION_OPTION = "ALERT_THRESHOLD_CONDITION_OPTION";

export const ALERT_PERIOD_DATE_YEAR = "date_year";
export const ALERT_PERIOD_DATE_YTD = "date_ytd";
export const ALERT_PERIOD_DATE_WEEK = "date_week";
export const ALERT_PERIOD_DATE_MONTH = "date_month";
export const ALERT_PERIOD_DATE_QUARTER = "date_quarter";
export const ALERT_PERIOD_DATE_RANGE = "date_range";
export const ALERT_PERIOD_NONE = "none";
export const ALERT_PERIOD_LAST_N = "last_n";

export const ALERT_PERIOD_LAST_N_UNITS = ["DAY", "WEEK", "MONTH", "QUARTER", "YEAR"] as const;
export const ALERT_PERIOD_CURRENT_UNITS = ["WEEK", "MONTH", "QUARTER", "YEAR"] as const;

export const ALERT_EVALUATION_SCOPE_INDIVIDUAL = "individual";
export const ALERT_EVALUATION_SCOPE_GROUPED = "grouped";
export const ALERT_EVALUATION_SCOPE_DYNAMIC = "dynamic";

/** How a triggered scope is resolved: this incident only, for a chosen period, or until undone. */
export const ALERT_SCOPE_RESOLVE_ONCE = "once";
export const ALERT_SCOPE_RESOLVE_TEMPORARY = "temporary";
export const ALERT_SCOPE_RESOLVE_PERMANENT = "permanent";

export type AlertScopeResolveMode =
    | typeof ALERT_SCOPE_RESOLVE_ONCE
    | typeof ALERT_SCOPE_RESOLVE_TEMPORARY
    | typeof ALERT_SCOPE_RESOLVE_PERMANENT;

export const ALERT_SCOPE_RESOLVE_DURATION_UNITS = ["HOUR", "DAY", "WEEK", "MONTH"] as const;

export type AlertScopeResolveDurationUnit = typeof ALERT_SCOPE_RESOLVE_DURATION_UNITS[number];

/**
 * Cooldown after a trigger is entered as amount + unit and stored on the alert in hours. Months are left out on purpose:
 * they have no fixed length in hours, so the entered unit could not be shown back reliably.
 */
export const ALERT_COOLDOWN_UNITS = ["HOUR", "DAY", "WEEK"] as const;

export type AlertCooldownUnit = typeof ALERT_COOLDOWN_UNITS[number];

const ALERT_COOLDOWN_UNIT_HOURS: Record<AlertCooldownUnit, number> = { HOUR: 1, DAY: 24, WEEK: 168 };

export const ALERT_COOLDOWN_DEFAULT_AMOUNT = 12;
export const ALERT_COOLDOWN_DEFAULT_UNIT: AlertCooldownUnit = "HOUR";

export const toAlertCooldownHours = (amount: number | null | undefined, unit: AlertCooldownUnit): number =>
    (amount != null && amount > 0 ? Math.round(amount) * ALERT_COOLDOWN_UNIT_HOURS[unit] : 0);

/** Largest unit that fits the stored hours exactly, so 168 hours comes back as 1 week and 36 hours stays 36 hours. */
export const splitAlertCooldownHours = (
    hours: number | null | undefined,
): { amount: number | null; unit: AlertCooldownUnit } => {
    if (!hours || hours <= 0) {
        return { amount: null, unit: ALERT_COOLDOWN_DEFAULT_UNIT };
    }
    const unit = [...ALERT_COOLDOWN_UNITS].reverse().find((candidate) => hours % ALERT_COOLDOWN_UNIT_HOURS[candidate] === 0)
        ?? ALERT_COOLDOWN_DEFAULT_UNIT;
    return { amount: hours / ALERT_COOLDOWN_UNIT_HOURS[unit], unit };
};

export const isDynamicAlertScopeMode = (mode?: string | null): boolean =>
    `${mode || ""}`.toLowerCase() === ALERT_EVALUATION_SCOPE_DYNAMIC;

export const isDynamicAlertScope = (alert?: {
    evaluationScopeMode?: string | null;
    evaluationCriteria?: { evaluationScopeMode?: string | null };
} | null): boolean =>
    isDynamicAlertScopeMode(alert?.evaluationScopeMode || alert?.evaluationCriteria?.evaluationScopeMode);

export type AlertEvaluationScopeMode =
    | typeof ALERT_EVALUATION_SCOPE_INDIVIDUAL
    | typeof ALERT_EVALUATION_SCOPE_GROUPED
    | typeof ALERT_EVALUATION_SCOPE_DYNAMIC;

export type AlertEvaluationScopeRow = {
    uuid?: string;
    parameter?: string;
    name?: string;
    values: string[];
    label?: string;
    permanentResolve?: boolean;
};

export type AlertEvaluationScopePayload = {
    evaluationScopeMode?: AlertEvaluationScopeMode;
    evaluationScopes: Array<{
        uuid?: string;
        parameter: string;
        name?: string;
        values: string[];
        permanentResolve?: boolean;
    }>;
};

export const hasAlertEvaluationScopeOverlap = (scopes: AlertEvaluationScopeRow[]): boolean => {
    const seen = new Set<string>();
    for (const scope of scopes || []) {
        for (const value of scope.values || []) {
            const normalized = `${value}`.trim();
            if (!normalized) {
                continue;
            }
            if (seen.has(normalized)) {
                return true;
            }
            seen.add(normalized);
        }
    }
    return false;
};

export const toAlertEvaluationScopePayload = (
    parameter: string,
    mode: AlertEvaluationScopeMode,
    scopes: AlertEvaluationScopeRow[],
    permanentResolve = false,
): AlertEvaluationScopePayload => {
    const trimmedParameter = `${parameter || ""}`.trim();
    if (!trimmedParameter) {
        return { evaluationScopes: [] };
    }
    if (mode === ALERT_EVALUATION_SCOPE_DYNAMIC) {
        return {
            evaluationScopeMode: mode,
            evaluationScopes: [{ parameter: trimmedParameter, values: [], permanentResolve }],
        };
    }
    const rows = (scopes || [])
        .map((scope) => ({
            uuid: scope.uuid,
            parameter: trimmedParameter,
            name: `${scope.name || ""}`.trim() || undefined,
            values: (scope.values || []).map((value) => `${value}`.trim()).filter(Boolean),
            permanentResolve,
        }))
        .filter((scope) => scope.values.length > 0);
    if (rows.length === 0) {
        return { evaluationScopes: [] };
    }
    return {
        evaluationScopeMode: mode,
        evaluationScopes: rows,
    };
};

export const isAlertDateRangePeriod = (code?: string | null): boolean =>
    `${code || ""}`.toLowerCase() === ALERT_PERIOD_DATE_RANGE;

export const isAlertEmptyPeriod = (code?: string | null): boolean => {
    const normalized = `${code || ""}`.toLowerCase();
    return !normalized || normalized === ALERT_PERIOD_NONE;
};

export const isAlertLastNPeriod = (code?: string | null): boolean =>
    `${code || ""}`.toLowerCase() === ALERT_PERIOD_LAST_N;

export const isAlertCurrentYearPeriod = (code?: string | null): boolean => {
    const normalized = `${code || ""}`.toLowerCase();
    return normalized === ALERT_PERIOD_DATE_YEAR || normalized === ALERT_PERIOD_DATE_YTD;
};

export const isAlertCurrentPeriod = (code?: string | null): boolean => {
    const normalized = `${code || ""}`.toLowerCase();
    return normalized === ALERT_PERIOD_DATE_WEEK
        || normalized === ALERT_PERIOD_DATE_MONTH
        || normalized === ALERT_PERIOD_DATE_QUARTER
        || isAlertCurrentYearPeriod(normalized);
};

export const currentUnitFromPeriod = (
    code?: string | null,
): typeof ALERT_PERIOD_CURRENT_UNITS[number] => {
    switch (`${code || ""}`.toLowerCase()) {
        case ALERT_PERIOD_DATE_WEEK:
            return "WEEK";
        case ALERT_PERIOD_DATE_MONTH:
            return "MONTH";
        case ALERT_PERIOD_DATE_QUARTER:
            return "QUARTER";
        default:
            return "YEAR";
    }
};

export const periodCodeForCurrentUnit = (
    unit?: string | null,
): string => {
    switch (`${unit || ""}`.toUpperCase()) {
        case "WEEK":
            return ALERT_PERIOD_DATE_WEEK;
        case "MONTH":
            return ALERT_PERIOD_DATE_MONTH;
        case "QUARTER":
            return ALERT_PERIOD_DATE_QUARTER;
        default:
            return ALERT_PERIOD_DATE_YEAR;
    }
};

export type AlertPeriodMode =
    | typeof ALERT_PERIOD_NONE
    | typeof ALERT_PERIOD_LAST_N
    | typeof ALERT_PERIOD_DATE_RANGE
    | typeof ALERT_PERIOD_DATE_YEAR;

export const ALERT_PERIOD_MODES: AlertPeriodMode[] = [
    ALERT_PERIOD_NONE,
    ALERT_PERIOD_LAST_N,
    ALERT_PERIOD_DATE_RANGE,
    ALERT_PERIOD_DATE_YEAR,
];

export const resolveAlertPeriodMode = (code?: string | null): AlertPeriodMode => {
    if (isAlertEmptyPeriod(code)) {
        return ALERT_PERIOD_NONE;
    }
    if (isAlertDateRangePeriod(code)) {
        return ALERT_PERIOD_DATE_RANGE;
    }
    if (isAlertCurrentPeriod(code)) {
        return ALERT_PERIOD_DATE_YEAR;
    }
    return ALERT_PERIOD_LAST_N;
};

export const rollingDefaultsFromPeriod = (
    code?: string | null,
    periodValue?: EvaluationPeriodValue | null,
): { amount: number; unit: typeof ALERT_PERIOD_LAST_N_UNITS[number] } => {
    if (periodValue?.lastNAmount && periodValue.lastNAmount > 0 && periodValue.lastNUnit) {
        const unit = ALERT_PERIOD_LAST_N_UNITS.includes(periodValue.lastNUnit as typeof ALERT_PERIOD_LAST_N_UNITS[number])
            ? periodValue.lastNUnit as typeof ALERT_PERIOD_LAST_N_UNITS[number]
            : "MONTH";
        return { amount: periodValue.lastNAmount, unit };
    }
    switch (`${code || ""}`.toLowerCase()) {
        case "hour":
        case "hourly":
        case "day":
        case "daily":
            return { amount: 1, unit: "DAY" };
        case "week":
        case "weekly":
            return { amount: 1, unit: "WEEK" };
        case "last_2_weeks":
            return { amount: 2, unit: "WEEK" };
        case "month":
        case "monthly":
            return { amount: 1, unit: "MONTH" };
        case "last_3_months":
            return { amount: 3, unit: "MONTH" };
        case "last_6_months":
            return { amount: 6, unit: "MONTH" };
        case "quarter":
        case "quarterly":
            return { amount: 1, unit: "QUARTER" };
        case "year":
        case "yearly":
            return { amount: 1, unit: "YEAR" };
        case "last_2_years":
            return { amount: 2, unit: "YEAR" };
        case "last_3_years":
            return { amount: 3, unit: "YEAR" };
        case "last_5_years":
            return { amount: 5, unit: "YEAR" };
        default:
            return { amount: 1, unit: "WEEK" };
    }
};

export const parseAlertIsoDate = (value?: string | null): Date | undefined => {
    if (!value) {
        return undefined;
    }
    const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const startOfLocalDate = (value?: Date | null): Date => {
    const date = value ? new Date(value.getTime()) : new Date();
    date.setHours(0, 0, 0, 0);
    return date;
};

export const parseAlertStartDate = (isoDate?: string | null, instant?: string | null): Date => {
    const fromDate = parseAlertIsoDate(isoDate);
    if (fromDate) {
        return startOfLocalDate(fromDate);
    }
    if (instant) {
        const parsed = new Date(instant);
        if (!Number.isNaN(parsed.getTime())) {
            return startOfLocalDate(parsed);
        }
    }
    return startOfLocalDate();
};

export const formatAlertIsoDate = (value?: Date | null): string | undefined => {
    if (!value || Number.isNaN(value.getTime())) {
        return undefined;
    }
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export type EvaluationPeriodValue = {
    dateFrom?: string;
    dateTill?: string;
    dateYear?: number;
    throughNow?: boolean;
    lastNAmount?: number;
    lastNUnit?: string;
};

export const toEvaluationPeriodValue = (
    periodCode?: string | null,
    dateFrom?: Date | null,
    dateTill?: Date | null,
    dateYear?: number | null,
    throughNow?: boolean | null,
    lastNAmount?: number | null,
    lastNUnit?: string | null,
): EvaluationPeriodValue | undefined => {
    const value: EvaluationPeriodValue = {};
    if (throughNow != null) {
        value.throughNow = throughNow;
    }
    if (isAlertDateRangePeriod(periodCode)) {
        const from = formatAlertIsoDate(dateFrom);
        const till = throughNow ? undefined : formatAlertIsoDate(dateTill);
        if (from) {
            value.dateFrom = from;
        }
        if (till) {
            value.dateTill = till;
        }
    }
    if (`${periodCode || ""}`.toLowerCase() === ALERT_PERIOD_DATE_YEAR && dateYear != null) {
        value.dateYear = dateYear;
    }
    if (isAlertLastNPeriod(periodCode) && lastNAmount != null && lastNAmount > 0 && lastNUnit) {
        value.lastNAmount = lastNAmount;
        value.lastNUnit = lastNUnit;
    }
    if (Object.keys(value).length === 0) {
        return undefined;
    }
    return value;
};

export const sortAlertEvaluationPeriodOptions = <T extends { code?: string | null }>(
    periods: T[],
): T[] => {
    const rank = (code?: string | null): number => {
        const normalized = `${code || ""}`.toLowerCase();
        if (!normalized || normalized === ALERT_PERIOD_NONE) {
            return 0;
        }
        if (normalized === ALERT_PERIOD_LAST_N) {
            return 2;
        }
        return 1;
    };
    return [...(periods || [])].sort((left, right) => rank(left?.code) - rank(right?.code));
};

export const formatAlertEvaluationPeriodLabel = (
    period?: { name?: string; code?: string } | null,
    periodValue?: EvaluationPeriodValue | null,
    labels?: { none?: string; throughNow?: string },
): string => {
    if (isAlertEmptyPeriod(period?.code)) {
        return labels?.none || period?.name || "Alles";
    }
    let label = period?.name || period?.code || "";
    if (isAlertDateRangePeriod(period?.code) && (periodValue?.dateFrom || periodValue?.dateTill)) {
        if (periodValue.dateFrom && periodValue.dateTill) {
            label = `${label} (${periodValue.dateFrom} – ${periodValue.dateTill})`;
        } else if (periodValue.dateFrom) {
            label = `${label} (${periodValue.dateFrom})`;
        } else {
            label = `${label} (– ${periodValue.dateTill})`;
        }
    } else if (`${period?.code || ""}`.toLowerCase() === ALERT_PERIOD_DATE_YEAR && periodValue?.dateYear != null) {
        label = `${label} (${periodValue.dateYear})`;
    } else if (isAlertCurrentPeriod(period?.code) && `${period?.code || ""}`.toLowerCase() !== ALERT_PERIOD_DATE_YEAR) {
        label = period?.name || period?.code || label;
    } else if ((isAlertLastNPeriod(period?.code) || resolveAlertPeriodMode(period?.code) === ALERT_PERIOD_LAST_N)
        && periodValue?.lastNAmount && periodValue.lastNUnit) {
        label = `${label} (${periodValue.lastNAmount} ${periodValue.lastNUnit.toLowerCase()})`;
    }
    if (periodValue?.throughNow) {
        label = `${label}${labels?.throughNow || " tot nu"}`;
    }
    return label;
};

export const ALERT_STATUS_ACTIVE = "active";
export const ALERT_STATUS_OPEN = "open";
export const ALERT_STATUS_IN_PROGRESS = "in_progress";
export const ALERT_STATUS_RESOLVED = "resolved";

export const isAlertIncidentOpen = (statusCode?: string | null): boolean => {
    const code = `${statusCode || ""}`.trim().toLowerCase();
    return code === ALERT_STATUS_OPEN || code === ALERT_STATUS_IN_PROGRESS;
};

export const ALERT_MEASUREMENT_DISPLAY_FRACTION_DIGITS = 2;

export const averageAlertPreviewValue = (
    value?: number | null,
    scopes?: Array<{ value?: number | null }> | null,
): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    const scopeValues = (scopes || [])
        .map((scope) => scope?.value)
        .filter((scopeValue): scopeValue is number => typeof scopeValue === "number" && Number.isFinite(scopeValue));
    if (scopeValues.length === 0) {
        return null;
    }
    return scopeValues.reduce((total, scopeValue) => total + scopeValue, 0) / scopeValues.length;
};

export const formatAlertMeasurementValue = (value?: number | null): string => {
    if (value == null || !Number.isFinite(Number(value))) {
        return "—";
    }
    return Number(Number(value).toFixed(ALERT_MEASUREMENT_DISPLAY_FRACTION_DIGITS)).toString();
};
